const { useState, useEffect, useMemo } = React;
const useParams = window.ReactRouterDOM.useParams;
const useHistory = window.ReactRouterDOM.useHistory;
import { analyticsConversionsPath } from "../../Functions/domainPathSegments.js";
import StickyPageTitle from "../../Components/Header/Sticky/index.js";
import { ScannerHost } from "../../API/host.js";
import { useAnalyticsPage, KpiCard, authHeaders, useAnalyticsReport, toIsoDate, pctChange, formatPercent } from "./_shared.js";
import { IconTarget, IconTrendingUp, IconGlobe } from "./Icons.js";
import AnalyticsWorldMap from "./AnalyticsWorldMap.js";
import ConversionsPanel, { AUTO_EVENT_NAMES } from "./Conversions.js";
import TimeToConvert from "./TimeToConvert.js";
import ConversionChannels from "./ConversionChannels.js";
import ConversionCampaigns from "./ConversionCampaigns.js";
import FunnelBuilder from "./FunnelBuilder.js";
import TrendLineChart from "./TrendLineChart.js";
import "./Analytics.css";

// Countries with fewer total events than this are excluded from the rate
// view — otherwise a country with 1 visit + 1 conversion reads as a
// misleading 100% and can crowd out countries with a real sample size.
const MAP_RATE_MIN_SAMPLE = 5;

const CHANNEL_LABEL = { organic: "Organic", paid: "Paid", paid_social: "Paid Social (Meta)", referral: "Referral", direct: "Direct" };
const CHANNEL_COLORS_FIRST = {
    organic:     "rgba(74,222,128,0.55)",
    paid:        "rgba(251,146,60,0.55)",
    paid_social: "rgba(24,119,242,0.55)",
    referral:    "rgba(99,179,237,0.55)",
    direct:      "rgba(167,139,250,0.55)",
};
const CHANNEL_COLORS_LAST = {
    organic:     "rgba(74,222,128,0.85)",
    paid:        "rgba(251,146,60,0.85)",
    paid_social: "rgba(24,119,242,0.85)",
    referral:    "rgba(99,179,237,0.85)",
    direct:      "rgba(167,139,250,0.85)",
};

function AttributionComparison({ firstTouchByChannel, lastTouchByChannel }) {
    const channels = Array.from(new Set([
        ...(firstTouchByChannel || []).map(r => r.channel),
        ...(lastTouchByChannel  || []).map(r => r.channel),
    ]));

    const firstMap = Object.fromEntries((firstTouchByChannel || []).map(r => [r.channel, r.count]));
    const lastMap  = Object.fromEntries((lastTouchByChannel  || []).map(r => [r.channel, Number(r.sessions)]));

    const firstTotal = Object.values(firstMap).reduce((s, n) => s + n, 0);
    const lastTotal  = Object.values(lastMap).reduce((s, n) => s + n, 0);

    if (!firstTotal && !lastTotal) return null;

    return (
        <div className="sa-panel sa-attr-comparison">
            <h3 className="sa-panel__title">First-touch vs Last-touch Attribution</h3>
            <p className="sa-panel__sub">
                First-touch credits the channel where the session started. Last-touch credits the channel
                of the last event before a conversion — useful for bottom-of-funnel insight.
            </p>
            <div className="sa-attr-cols">
                <div className="sa-attr-col">
                    <h4 className="sa-panel__sub-title">First-touch</h4>
                    <div className="sa-consent-list">
                        {channels.map(ch => {
                            const count = firstMap[ch] || 0;
                            const pct = firstTotal > 0 ? Math.round((count / firstTotal) * 100) : 0;
                            return (
                                <div key={ch} className="sa-consent-row">
                                    <span className="sa-consent-row__label">{CHANNEL_LABEL[ch] || ch}</span>
                                    <div className="sa-bar">
                                        <div className="sa-bar__seg"
                                            style={{ width: pct + "%", background: CHANNEL_COLORS_FIRST[ch] || "rgba(192,159,83,0.5)" }}
                                            title={`${count} sessions`} />
                                    </div>
                                    <span className="sa-consent-row__pct">{pct}%</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="sa-attr-col">
                    <h4 className="sa-panel__sub-title">Last-touch</h4>
                    <div className="sa-consent-list">
                        {channels.map(ch => {
                            const count = lastMap[ch] || 0;
                            const pct = lastTotal > 0 ? Math.round((count / lastTotal) * 100) : 0;
                            return (
                                <div key={ch} className="sa-consent-row">
                                    <span className="sa-consent-row__label">{CHANNEL_LABEL[ch] || ch}</span>
                                    <div className="sa-bar">
                                        <div className="sa-bar__seg"
                                            style={{ width: pct + "%", background: CHANNEL_COLORS_LAST[ch] || "rgba(192,159,83,0.8)" }}
                                            title={`${count} sessions`} />
                                    </div>
                                    <span className="sa-consent-row__pct">{pct}%</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

const SECTIONS = [
    { key: "overview", label: "Overview" },
    { key: "deepdive", label: "Funnel & Sources" },
    { key: "setup",    label: "Events & Tracking" },
];

// Total traffic cross-check for the Conversion Rate tile — the platform's
// own conversion rate is necessarily computed over consent-linked sessions
// only (see the "session-linked conversions only" note throughout this
// page), so it's a rate over a sample, not all traffic. This surfaces GA4's
// full session count alongside it as a labeled reference point, not a
// replacement — same connected-account pattern GoogleAnalytics.js already
// uses (ad-connections → ad-daily-data's GA4 summary).
function useGa4TotalSessions(domain, fromIso, toIso) {
    const [state, setState] = useState({ connected: false, sessions: null, loading: false });

    useEffect(() => {
        if (!domain) { setState({ connected: false, sessions: null, loading: false }); return; }
        let ignore = false;
        setState(s => ({ ...s, loading: true }));
        fetch(`${ScannerHost}/api/ad-connections?domain=${encodeURIComponent(domain)}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (ignore) return null;
                const hasGa4 = (data?.connections || []).some(c => c.platform === "google_analytics" && c.account_id);
                if (!hasGa4) { setState({ connected: false, sessions: null, loading: false }); return null; }
                const qs = `platform=google_analytics&domain=${encodeURIComponent(domain)}&fromDate=${fromIso}&toDate=${toIso}`;
                return fetch(`${ScannerHost}/api/ad-daily-data?${qs}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null);
            })
            .then(daily => {
                if (ignore || !daily) return;
                setState({ connected: true, sessions: Number(daily.summary?.sessions || 0), loading: false });
            })
            .catch(() => { if (!ignore) setState(s => ({ ...s, loading: false })); });
        return () => { ignore = true; };
    }, [domain, fromIso, toIso]);

    return state;
}

export default function AnalyticsConversionsOverview() {
    document.title = "Conversions | Site Analytics";

    const { section: sectionParam } = useParams();
    const history = useHistory();

    const {
        domain, hasDomain, getLastDays, setLastDays, fromDate, setFromDate, toDate, setToDate,
        fromIso, toIso, tick, setTick, data, loading, error, showData,
    } = useAnalyticsPage();
    const ga4 = useGa4TotalSessions(domain, fromIso, toIso);

    // Previous period of the same length, immediately preceding the current
    // range — same "vs previous period" pattern the Overview page's KPI
    // cards use (src/Pages/Analytics/index.js).
    const prevRange = useMemo(() => {
        const spanMs = toDate.getTime() - fromDate.getTime();
        const prevTo = new Date(fromDate.getTime() - 24 * 60 * 60 * 1000);
        const prevFrom = new Date(prevTo.getTime() - spanMs);
        return { fromIso: toIsoDate(prevFrom), toIso: toIsoDate(prevTo) };
    }, [fromDate, toDate]);
    const { data: prevData } = useAnalyticsReport(domain, prevRange.fromIso, prevRange.toIso, tick);

    const section = SECTIONS.some(s => s.key === sectionParam) ? sectionParam : "overview";

    const manualConversions = useMemo(
        () => (data?.conversions || []).filter(c => !AUTO_EVENT_NAMES.has(c.name)),
        [data]
    );

    const totalConversions = useMemo(
        () => manualConversions.reduce((s, c) => s + (c.count || 0), 0),
        [manualConversions]
    );

    const prevTotalConversions = useMemo(
        () => (prevData?.conversions || []).filter(c => !AUTO_EVENT_NAMES.has(c.name)).reduce((s, c) => s + (c.count || 0), 0),
        [prevData]
    );

    const trendTotalConversions = useMemo(
        () => pctChange(totalConversions, prevTotalConversions),
        [totalConversions, prevTotalConversions]
    );
    const trendConversionRate = useMemo(
        () => pctChange(data?.totals?.conversionRate, prevData?.totals?.conversionRate),
        [data, prevData]
    );

    const trendData = useMemo(
        () => (data?.dailyConversions || []).map(d => ({ date: d.date, num: d.count })),
        [data]
    );

    const [mapMode, setMapMode] = useState("rate");

    const rateCountries = useMemo(() => {
        const totalByCode = new Map((data?.countries || []).map(c => [c.code, c.events]));
        const convByCode  = new Map((data?.conversionCountries || []).map(c => [c.code, c.events]));
        const out = [];
        totalByCode.forEach((total, code) => {
            if (total < MAP_RATE_MIN_SAMPLE) return;
            const conv = convByCode.get(code) || 0;
            out.push({ code, events: Math.round((conv / total) * 1000) / 10 });
        });
        return out;
    }, [data]);

    const mapCountries = mapMode === "rate" ? rateCountries : data?.conversionCountries;

    return (
        <div style={{ flex: "1", minWidth: 0 }}>
            <StickyPageTitle
                title="Conversions"
                numberofDays={setLastDays}
                getLastDays={getLastDays}
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
            />
            <div className="dashboard-content">
                <div className="sa-page">
                    {!hasDomain && (
                        <p className="sa-notice">Select a domain in the header to view conversions.</p>
                    )}
                    {hasDomain && loading && <p className="sa-notice">Loading&hellip;</p>}
                    {hasDomain && error && <p className="sa-notice sa-notice--error">{error}</p>}
                    {hasDomain && !loading && data?.noSiteKey && (
                        <p className="sa-notice">No analytics set up for this domain yet.</p>
                    )}
                    {hasDomain && !loading && data?.noData && (
                        <p className="sa-notice">No data for the selected period.</p>
                    )}

                    {showData && (
                        <div className="sa-conv-layout">
                            <nav className="sa-conv-submenu" aria-label="Conversions view">
                                <ul className="sa-conv-submenu__list">
                                    {SECTIONS.map(s => (
                                        <li key={s.key} className="sa-conv-submenu__item">
                                            <button
                                                type="button"
                                                className={"sa-conv-submenu__link" + (section === s.key ? " --active" : "")}
                                                onClick={() => history.push(analyticsConversionsPath(domain, s.key))}
                                            >
                                                {s.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className="sa-conv-submenu-content">
                            {section === "overview" && (
                                <div className="sa-conv-grid sa-conv-grid--overview">
                                    <KpiCard className="sa-conv-kpi1"
                                        icon={<IconTarget />}
                                        label="Total conversions"
                                        value={totalConversions.toLocaleString("de-DE")}
                                        sub={`across ${manualConversions.length} registered event${manualConversions.length !== 1 ? "s" : ""}`}
                                        trend={trendTotalConversions}
                                    />
                                    <KpiCard className="sa-conv-kpi2"
                                        icon={<IconTrendingUp />}
                                        label="Conversion rate"
                                        value={formatPercent(data.totals.conversionRate)}
                                        trend={trendConversionRate}
                                        sub={
                                            <>
                                                {`${data.totals.convertedSessions.toLocaleString("de-DE")} of ${data.totals.uniqueSessions.toLocaleString("de-DE")} consent-linked sessions`}
                                                {ga4.connected && ga4.sessions != null && (
                                                    <span className="sa-kpi__cross-check">
                                                        GA4 total traffic: {ga4.sessions.toLocaleString("de-DE")} sessions
                                                        (rate above covers consent-linked sessions only)
                                                    </span>
                                                )}
                                            </>
                                        }
                                    />

                                    <div className="sa-panel sa-conv-trend">
                                        <h3 className="sa-panel__title"><IconTrendingUp className="sa-icon" /> Conversion trend</h3>
                                        <TrendLineChart
                                            data={trendData}
                                            title="Conversions"
                                            showInsights
                                            height={260}
                                        />
                                    </div>

                                    <div className="sa-panel sa-conv-map">
                                        <h3 className="sa-panel__title">
                                            <IconGlobe className="sa-icon" /> Where conversions happen
                                            <span className="sa-map__mode-toggle" role="group" aria-label="Map weighting">
                                                <button
                                                    type="button"
                                                    className={mapMode === "rate" ? "is-active" : ""}
                                                    onClick={() => setMapMode("rate")}
                                                >
                                                    Rate
                                                </button>
                                                <button
                                                    type="button"
                                                    className={mapMode === "volume" ? "is-active" : ""}
                                                    onClick={() => setMapMode("volume")}
                                                >
                                                    Volume
                                                </button>
                                            </span>
                                        </h3>
                                        <AnalyticsWorldMap
                                            countries={mapCountries}
                                            metricLabel={mapMode === "rate" ? "Conversion rate" : "Conversions"}
                                            formatValue={mapMode === "rate" ? formatPercent : undefined}
                                        />
                                    </div>
                                </div>
                            )}

                            {section === "deepdive" && (
                                <div className="sa-conv-grid sa-conv-grid--deepdive">
                                    <TimeToConvert
                                        timeToConvert={data.timeToConvert}
                                        totalConversions={totalConversions}
                                    />

                                    <ConversionChannels
                                        byChannel={data.conversionsByChannel}
                                        byDevice={data.conversionsByDevice}
                                    />

                                    <FunnelBuilder
                                        domain={domain}
                                        fromIso={fromIso}
                                        toIso={toIso}
                                    />

                                    <ConversionCampaigns
                                        domain={domain}
                                        fromIso={fromIso}
                                        toIso={toIso}
                                        byCampaign={data.conversionsByCampaign}
                                    />

                                    <AttributionComparison
                                        firstTouchByChannel={data.conversionsByChannel}
                                        lastTouchByChannel={data.lastTouchByChannel}
                                    />
                                </div>
                            )}

                            {section === "setup" && (
                                <ConversionsPanel
                                    domain={domain}
                                    conversions={data.conversions}
                                    onDefsChanged={() => setTick(t => t + 1)}
                                />
                            )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
